import { computed, ref } from "@vue/composition-api";
import router from "@/router";
import store from "@/store";
import axios from "axios";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import markdownit from 'markdown-it'
import moment from "moment";
import { v4 as uuidv4 } from 'uuid';

export function useChatbot() {
  const chat = computed(() => store.getters["chatbot/getChat"]);
  const md = markdownit()
  const myMessage = ref({
    from: 'me',
    completed: true,
    body: '',
    type: 'text'
  })
  const isScrollEnable = ref(false)
  const viewHasSidebar = computed(() => store.getters["chatbot/getViewHasSidenav"]);

  const onScroll = ()=> {
    isScrollEnable.value = true
  }

  const goToBottom = ()=> {
    if (isScrollEnable.value) return
    setTimeout(() => {
      const container = document.getElementById("chatBotResponses");
      container.scrollTo({
        top: container.scrollHeight
      });
    }, 100);
  }

  const sendQueryToApi = (text)=> {
    const query = text.replace(/[\n\r]/g, '');
    axios.post(`${window.__env.VUE_APP_MS_IA_BOT_URL}/omniview_assistant/process_query`, { query }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        "x-service-token": window.__env.VUE_APP_SERVICE_TOKEN_IA
      },
    })
    .then(({data}) => {
      setTypesOfMessages(data.detail)
    })
    .catch(() => {
      store.commit("chatbot/addMessage", {
        from: 'bot',
        completed: true,
        body: 'Lo sentimos, hubo un problema. Inténtalo de nuevo más tarde',
        type: 'text'
      });
    }).finally(() => {
      store.commit("chatbot/updateLoadinStatus", false);
    });
  }

  const setTypesOfMessages = (data)=> {
    if (!data) return

    const stringMarkdownToHTLM = md.render(data.message);
    if (data.type === 'text' || data.type === 'link') {
      store.commit("chatbot/addMessage", {
        from: 'bot',
        completed: true,
        body: stringMarkdownToHTLM,
        type: 'text',
        alreadyShown: false
      });
      goToSection(data)
      return
    }

    if (data.type === 'chart' || data.type === 'list') {
      const chartDataId = uuidv4();
      const isDoughnutChart = data.values.chart === 'donut' || data.values.chart === 'doughnut'
      let field = data.values.field;

      if (field === "creation_date") {
        switch (data.values.model) {
          case "Alarm":
            field = "created";
            break;

          case "DetectionEvent":
            field = "detection_events.event_created_at";
            break;

          default:
            field = "created_at";
        }
      }

      store.commit("chatbot/addMessage", {
        from: 'bot',
        completed: true,
        body: stringMarkdownToHTLM,
        type: 'card',
        value: {
          chart_id: chartDataId,
          chart: isDoughnutChart ? 'pie' : data.values.chart,
          field: field,
          filters: setFilters(data.values.filters, data.values),
          id: chartDataId,
          model: data.values.model,
          name: data.values.model
        }
      });
    }

  }

  const setFilters = (filters, data)=> {
    const keys = Object.keys(filters)
    let formatFilters = {}
    keys.forEach(filter => {
      if (filters[filter] === null || filters[filter] === 'null') return
      if (filter === 'from' || filter === 'to') {
        let formatDate = moment(filters[filter]).utc().format("YYYY-MM-DD HH:mm:ss");
        if (data.model === "DetectionEvent") {
          formatDate = moment(filters[filter])
            .utc()
            .format("YYYY-MM-DD[T]HH:mm");
        }
        formatFilters[`${filter}`] = formatDate
        return
      }
      if(filter === "cameras") {
        const camerasIds = getCameraIdByName(filters[filter])
        formatFilters["id_camera_proxy"] = camerasIds
        return
      }

      if(filter === "tenants") {
        const tenantsIds = getTenantIdByName(filters[filter])
        formatFilters["id_tenant"] = tenantsIds
        return
      }

      if (filter === "metadata") {

        if (data.model === "DetectionEvent") {
          const filtersMap = {
            'equal_to': 'equal_to',
            'not_equal_to': 'not_equal_to',
            'contains': 'contains',
            'not_contains': 'not_contains',
            'greater_than': 'gt',
            'lower_than': 'lt',
            'greater_than_or_equal': 'gte',
            'lower_than_or_equal': 'lte',
            'is_in': 'is_in',
            'is_not_in': 'is_not_in',
          };

          formatFilters.data = filters[filter].map(item =>
            item.replace(/^([^,]+),([^|]+)\|(.+)$/, (_, field, operator, value) => {
              const mappedOperator = filtersMap[operator] || operator;
              return `${field},${mappedOperator}|${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
            })
          );
        }  else {
          formatFilters.data = filters[filter].map(item =>
            item.replace(/equal_to\|(.+)$/, (_, value) =>
              `equal_to|${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`
            )
          );
        }

        return;
      }

      if (filter === "event_type") {
        formatFilters[filter] = filters[filter].map(value =>
          value.trim().toLowerCase().replace(/\s+/g, "_")
        );

        return;
      }
      if(filter === "facilities") {
        const unitsIds = getFacilitiesIdByName(filters[filter])
        formatFilters["id_unit"] = unitsIds
        return
      }

      formatFilters[`${filter}`] = filters[filter]
    });
    return formatFilters
  }

  const getTenantIdByName = (filter) => {
    const tenants = store.getters["grid/getAllTenants"]
    let ids = []
    filter.forEach(tenantFilter => {
      const existsTenant = tenants.filter( tenant => tenant.name.toLowerCase() === tenantFilter.toLowerCase())
      if(existsTenant.length !== 0) ids.push(existsTenant[0].id.toString())
    });
    sendMessageIfNotExists(ids, 'Tenants')
    return ids
  }

  const getCameraIdByName = (filter) => {
    const normalize = str => str?.replace(/\s+/g, '').toLowerCase();
    const normalizedFilter = new Set(filter.map(normalize));
    const allUnits = (store.getters["grid/getAllUnist"] || []).flat().filter(Boolean);
    const ids = allUnits.flatMap(unit =>
      (unit.cameras || [])
        .filter(camera =>
          camera?.name && normalizedFilter.has(normalize(camera.name))
        )
        .map(camera => camera.camera_proxy_id ?? camera.id_camera_proxy)
        .filter(id => id != null)
        .map(id => id.toString())
    );
    const uniqueIds = [...new Set(ids)];
    sendMessageIfNotExists(uniqueIds, 'Cameras')
    return uniqueIds;
  }

  const getFacilitiesIdByName = (filter) => {
    const normalize = str => str?.replace(/\s+/g, '').toLowerCase();
    const normalizedFilter = new Set(filter.map(normalize));
    const allUnits = (store.getters["grid/getAllUnist"] || []).flat().filter(Boolean);
    const unitIds = allUnits
      .filter(unit =>
        unit?.description && normalizedFilter.has(normalize(unit.description))
      )
      .map(unit => unit.id.toString());
    const uniqueIds = [...new Set(unitIds)];
    sendMessageIfNotExists(uniqueIds, 'Facilities')
    return uniqueIds;
  }

  const sendMessageIfNotExists = (ids, type = 'Camera') => {
    if(!ids || ids.length === 0) {
      store.commit("chatbot/addMessage", {
        from: 'bot',
        completed: true,
        body: `The ${type} has not been found but here is the chart with the other filters`,
        type: 'text'
      });
    }
  }

  const goToSection = (data) => {
    if (data.type === 'link' && data.values !== null) {
      if(data.values[0].link === "https://sbo.omniview.cloud/#/login"
          || data.values[0].link === "sbo.omniview.cloud/#/login"
          ||  data.values[0].title === "Omniview SBO Login") return
      if(data.values[0].action === "create") {
        store.commit("chatbot/setViewSidebarActive", true);
        store.commit("chatbot/setViewHasSidenav", true);
      }
      router.push(data.values[0].link);
      revertSetViewHasSideber()
    }
  }

  const revertSetViewHasSideber = ()=> {
    setTimeout(() => {
      store.commit("chatbot/setViewSidebarActive", false);
    }, 1500);
  }

  const onDrag = (response)=> {
    setSlotData(response)
  }

  const setSlotData = (response)=> {
    const chartData = response.value
    const source = null

    if (response.value?.chart === 'list') {
      store.dispatch("grid/setSlotData", {
        type: SLOT_TYPES.CHARTS_SDK_DATA,
        name: `CHART SDK DATA`,
        data: chartData,
      });
      return
    }

    store.dispatch("grid/setSlotData", {
      type: SLOT_TYPES.CHARTS_SDK,
      name: `Charts SDK`,
      data: {
        chartData,
        in_app_source: source,
      },
      source: source ? source : SOURCES.ALARM
    });
  }

  const closeChat = (typeChat)=> {
    store.commit("chatbot/closeChatBot");
    if(typeChat === 'fake') return;
    deleteSession();
  }

  const deleteSession = () => {
    axios.delete(`${window.__env.VUE_APP_MS_IA_BOT_URL}/omniview_assistant/delete_session`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        "x-service-token": window.__env.VUE_APP_SERVICE_TOKEN_IA
      },
    })
  }

  const reloadChat = ()=> {
    store.commit("chatbot/reloadChat");
  }

  const messageHandler = (message)=> {
    if (message === '' || message.length === 1) return
    myMessage.value.body = message
    isScrollEnable.value = false
    store.commit("chatbot/updateLoadinStatus", true);
    store.commit("chatbot/addMessage", {...myMessage.value});
    if (message === 'modo super admin') {
      setLogSuperAdmin()
      return
    }
    sendQueryToApi(myMessage.value.body)
    myMessage.value.body = ''
  }

  const setLogSuperAdmin = ()=> {
    const logSuperAdmin = { from: 'bot', completed: true, body: 'Modo super admin activado', type: 'text' }
    store.commit("chatbot/addMessage", logSuperAdmin);
    store.commit("chatbot/updateLoadinStatus", false);
  }

  return {
      chat,
      closeChat,
      goToBottom,
      messageHandler,
      myMessage,
      onScroll,
      viewHasSidebar,
      reloadChat,
      onDrag
  }
}
